import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ClearancelevelEnum, ClearanceLevels } from 'src/app/constants/clearance-levels.enum';
import { MarkFormGroupTouchedAndDirty } from 'src/app/directives/mark-form-group-as-touched-dirty.function';
import { MinimumWordCountValidator } from 'src/app/directives/minimum-word-count-validator.directive';
import { SettingValueModel } from 'src/app/models/api/setting-value.model';
import { BuilderRequestAutoExportSettingsModel } from 'src/app/models/builder/builder-request-auto-export-settings.model';
import { BuilderRequestModel } from 'src/app/models/builder/builder-request.model';
import CloudItem from 'src/app/models/clouditem';
import { WorkflowListItem } from 'src/app/models/workflowlistitem';
import { WorkgroupInformationModel } from 'src/app/models/workgroup-information.model';
import { SettingsApiClient } from 'src/app/services/api-clients/settings.apiclient';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { DossierService } from 'src/app/services/dossier.service';
import { LoggingService } from 'src/app/services/logging.service';
import { TheNaviGator } from 'src/app/services/the-navi-gator.service';
import { WorkflowService, WorkFlowTypeEnum } from 'src/app/services/workflow.service';
import { ModalOpenerService } from '../modal-opener.service';
import { StatusRequestApiClient } from 'src/app/services/api-clients/status-request.api-client';
import { SingleOrDefault } from 'src/app/_helpers/ArrayExtensionMethodHelper.functions';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { BuilderRequestApiClient } from 'src/app/services/api-clients/builder-request.api-client';
import { AutoExportSettingsModel } from 'src/app/components/settings/components/auto-export/auto-export-settings.model';
import { OrganisationApiClient } from 'src/app/services/organisation.apiclient';
import { OrganisationPlugins } from 'src/app/constants/settings.enum';

export const NewRequestPickerModalOptions: NgbModalOptions = {
  backdrop: 'static',
  keyboard: false,
  centered: true,
  size: 'md'
};

@Component({
  templateUrl: './new-request-picker.modal.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class NewRequestPickerModal {
  private _request: BuilderRequestModel = new BuilderRequestModel();
  public defaultWorkflows: WorkflowListItem[] = [];
  public workflows: WorkflowListItem[] = [];
  public selectableWorkgroups: WorkgroupInformationModel[] = [];
  public allowAllRequestsTypes: boolean = true;
  public requestName: string = "";
  public disableOrganisationDropdown: boolean = false;
  public disableWorkflowDropdown: boolean = false;
  public newRequestMode: boolean = false;
  public createClicked: boolean = false;
  public saveClicked: boolean = false;
  public showAutoExport: boolean = false;
  private exportPlugins: OrganisationPlugins[] = [OrganisationPlugins.SharePoint, OrganisationPlugins.OneDrive, OrganisationPlugins.Teams, OrganisationPlugins.Hyarchis];

  requestRequestNameControl: FormControl;
  requestWorkflowControl: FormControl;
  requestWorkgroupControl: FormControl;
  requestClearanceControl: FormControl;
  form: UntypedFormGroup;

  get request(): BuilderRequestModel {
    return this._request;
  }
  @Input() set request(val: BuilderRequestModel) {
    this._request = val;

    this.setupRequestData();
  }

  get hasCloudItemSet(): boolean {
    if (!this._request) {
      return false;
    }
    return this._request.autoExportModel != null && this._request.autoExportModel != undefined && this._request.autoExportModel.cloudItem != null;
  }

  /**
   * :FRONTEND PROPERTY: Whether the Organisation default location should be used
   */
  get HasSpecificCloudItem(): boolean {
    if (!this.request) {
      return false;
    }
    return this.request.autoExportModel != null && this.request.autoExportModel != undefined && (this.request.autoExportModel.cloudItem != undefined && this.request.autoExportModel.cloudItem != null);
  };

  get selectedfolderPath(): string {
    if (!this.request || this.GetAutoExportPath() == "") {
      return this.translateService.instant("Request.RequestTitle.DisabledSinceNoLocation");
    }
    return this.GetAutoExportPath();
  }

  public hasEnabledPlugins: boolean = false;

  //Organisations
  public selectedOrganisationGuid: string = "";
  public selectedOrganisationName: string = "";

  //Workflow Type Filters
  private selectedRequestTypeGuid: string = "";
  public selectedRequestTypeName: string = "";
  public newRequestTypeName: string = "";
  get workflowsOfTypeGeneric(): WorkflowListItem[] {
    return this.workflows.filter(x => x.workflowType == WorkFlowTypeEnum.Generic);
  }
  get workflowsOfTypeTax(): WorkflowListItem[] {
    return this.workflows.filter(x => x.workflowType == WorkFlowTypeEnum.Tax);
  }
  get workflowsOfTypeChamberOfCommerce(): WorkflowListItem[] {
    return this.workflows.filter(x => x.workflowType == WorkFlowTypeEnum.ChamberOfCommerce);
  }
  get workflowsOfTypeSbrNexus(): WorkflowListItem[] {
    return this.workflows.filter(x => x.workflowType == WorkFlowTypeEnum.SbrNexus);
  }
  get workflowsOfTypeWwft(): WorkflowListItem[] {
    return this.workflows.filter(x => x.workflowType == WorkFlowTypeEnum.Wwft);
  }

  //Workgroups
  public selectedWorkgroupName: string = this.translateService.instant("Request.RequestTitle.Noworkgroup");
  private selectedWorkgroupGuid: string = "";

  //Clearancelevels
  public selectedClearanceLevel: ClearancelevelEnum = ClearancelevelEnum.One;
  public allowedClearanceLevels = ClearanceLevels;
  get getMaxClearanceLevelForWorkgroup(): ClearancelevelEnum {
    let workgroup = this.selectableWorkgroups.find(wg => wg.guid === this.selectedWorkgroupGuid);
    if (workgroup && workgroup.clearancelevel) {
      return workgroup.clearancelevel as ClearancelevelEnum;
    }
    return ClearancelevelEnum.One;
  }

  constructor(private activeModal: NgbActiveModal, private authenticationService: AuthenticationService, private loggingService: LoggingService,
    private settingsApiClient: SettingsApiClient, private workflowService: WorkflowService, private navigate: TheNaviGator,
    private requestService: DossierService, private formBuilder: UntypedFormBuilder, private translateService: TranslateService,
    private modalOpenerService: ModalOpenerService, private statusRequestApiClient: StatusRequestApiClient, private localStorageService: LocalStorageService,
    private builderRequestApiClient: BuilderRequestApiClient, private organisationApiClient: OrganisationApiClient) {
    this.settingsApiClient.getAutoExport(this.authenticationService.organisationGuid).subscribe({
      next: (autoExportSettings: AutoExportSettingsModel) => {
        this.showAutoExport = autoExportSettings.enabled;
      },
      error: (err) => {
        this.loggingService.logException(err);
      }
    });
    this.organisationApiClient.getEnabledPlugins(this.authenticationService.organisationGuid).subscribe({
      next: (enabledPluginsData: any) => {
        this.hasEnabledPlugins = enabledPluginsData.filter((p: any) => this.exportPlugins.includes(p.code)).length > 0;
      }
    });
    this.workflowService.getWorkflowList().then(values => {
      this.defaultWorkflows = values;
      return this.getWorkflows(this.authenticationService.organisationGuid);
    }).then(() => {
      if (this._request && this._request.workflowId) {
        this.changeWorkflowType(this.workflows.find(wf => wf.id === this._request!.workflowId)!);
      } else if (this.localStorageService.lastSelectedRequestType) {
        this.changeWorkflowType(this.workflows.find(wf => wf.id === this.localStorageService.lastSelectedRequestType)!);
      }
    })
      .catch(error => this.loggingService.logException(error));
    this.selectableWorkgroups = this.authenticationService.MyWorkgroups;

    this.requestRequestNameControl = new FormControl(this.newRequestTypeName,
      [
        Validators.minLength(4),
        Validators.maxLength(500),
        MinimumWordCountValidator(1)
      ]
    );
    this.requestWorkflowControl = new FormControl(this.selectedRequestTypeGuid,
      [
        Validators.required
      ]
    );
    this.requestWorkgroupControl = new FormControl(this.selectedWorkgroupName, []);
    this.requestClearanceControl = new FormControl<ClearancelevelEnum>(ClearancelevelEnum.One, []);
    this.form = this.formBuilder.group(
      {
        requestWorkflowControl: this.requestWorkflowControl,
        requestRequestNameControl: this.requestRequestNameControl,
        requestWorkgroupControl: this.requestWorkgroupControl,
        requestClearanceControl: this.requestClearanceControl,
      }
    );
  }


  public closeModal() {
    this.activeModal.close();
  }

  async saveRequest() {
    this.createClicked = true;
    if (this.form.invalid) {
      MarkFormGroupTouchedAndDirty(this.form);
      this.createClicked = false;
      return;
    }
    this.requestName = this.requestRequestNameControl.value || this.newRequestTypeName;
    this.localStorageService.lastSelectedRequestType = this.selectedRequestTypeGuid;
    (await this.requestService.createDossier(this.requestName, this.selectedWorkgroupGuid, this.selectedClearanceLevel, this.selectedRequestTypeGuid)).subscribe({
      next: (request: BuilderRequestModel) => {
        if (this.request.autoExportModel != null) {
          this.request.autoExportModel.requestGuid = request.id;
          this.builderRequestApiClient.SetAutoExport(this.request.autoExportModel!);
        }
        this.activeModal.close();
        this.openNewRequest(request.id);
      },
      error: (err) => {
        this.loggingService.logException(err);
        this.createClicked = false;
      }
    });
  }

  async updateRequest() {
    this.saveClicked = true;
    if (this.form.invalid) {
      MarkFormGroupTouchedAndDirty(this.form);
      this.saveClicked = false;
      return;
    }
    this.requestName = this.requestRequestNameControl.value || this.newRequestTypeName;
    (await this.requestService.updateDossier(this.request!.id, this.requestName, this.selectedWorkgroupGuid, this.selectedClearanceLevel)).subscribe({
      next: (request: BuilderRequestModel) => {
        if (this.request.autoExportModel != null) {
          this.request.autoExportModel.requestGuid = request.id;
          this.builderRequestApiClient.SetAutoExport(this.request.autoExportModel!);
        }
        this.activeModal.close();
        this.openNewRequest(request.id);
      },
      error: (err) => {
        this.loggingService.logException(err);
        this.saveClicked = false;
      }
    });
  }

  public openNewRequest(requestGuid: string) {
    this.navigate.To_Build(this.selectedRequestTypeGuid, requestGuid);
  }

  public changeWorkflowType(workflow: WorkflowListItem) {
    this.selectedRequestTypeGuid = workflow.id;
    this.selectedRequestTypeName = workflow.translationKey;
    this.newRequestTypeName = this.translateService.instant(workflow.translationNewRequestNameKey);
    this.requestWorkflowControl.setValue(this.selectedRequestTypeGuid)
  }

  public changeWorkgroup(workgroup: WorkgroupInformationModel | null = null) {
    this.selectedWorkgroupGuid = workgroup?.guid || "";
    this.selectedWorkgroupName = workgroup?.name || this.translateService.instant("Request.RequestTitle.Noworkgroup");
    if (!workgroup) {
      this.changeClearanceLevel(ClearancelevelEnum.One);
    }
    this.requestWorkgroupControl.setValue(workgroup);
    this.requestClearanceControl.setValue(ClearancelevelEnum.One);
  }

  public changeClearanceLevel(clearanceLevel: ClearancelevelEnum) {
    this.selectedClearanceLevel = clearanceLevel;
    this.requestClearanceControl.setValue(clearanceLevel);
  }

  public openFolderSelection() {
    const modalRef = this.modalOpenerService.CloudFolderPicker(this.request!.id, this.request!, this.requestName);
    modalRef.componentInstance.close.subscribe(async (setCloudItem: CloudItem) => {
      if (setCloudItem != null) {
        this.SetAutoExportSpecificCloudItem(setCloudItem);
      }
      modalRef.close();
    });
  }

  resetAutoExport() {
    this.SetAutoExportSpecificCloudItem(null);
    if (this._request) {
      this.statusRequestApiClient.SetAutoExport(this._request.autoExportModel!);
    }
  }

  /**
   * :FRONTEND PROPERTY: Whether auto export is enabled
   */
  private GetAutoExportEnabled(): boolean {
    if (!this.request) {
      return false;
    }
    return this.request.autoExportModel != null && this.request.autoExportModel != undefined && this.request.autoExportModel.enabled;
  }

  private GetAutoExportPath(): string {
    if (this.HasSpecificCloudItem) {// this.request.autoExportModel && this.request.autoExportModel.cloudItem) {
      return this.request!.autoExportModel!.cloudItem!.providerName + "/" + this.request!.autoExportModel!.cloudItem!.folderPath;
    }
    return "";
  }

  /**
   *  :FRONTEND PROPERTY: Sets Specific Cloud Item or resets with NULL
   */
  private SetAutoExportSpecificCloudItem(cloudItem: CloudItem | null) {
    if (!this._request) {
      return;
    }
    if (!this._request!.autoExportModel) {
      this._request.autoExportModel = new BuilderRequestAutoExportSettingsModel();
      this._request.autoExportModel.requestGuid = this._request.id;
    }
    this._request.autoExportModel.enabled = true;
    this._request.autoExportModel.cloudItem = cloudItem;
  }

  private getWorkflows(organisationGuid: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.settingsApiClient.getWorkflowSettings(organisationGuid).subscribe({
        next: (workflowSettings: SettingValueModel[]) => {
          this.workflows = [...this.defaultWorkflows];
          if (workflowSettings.length > 0) {
            workflowSettings.forEach(workflowSetting => {
              if (!workflowSetting.isEnabled) { // if disabled, return all others, thus filtering out the workflows associated with the setting
                this.workflows = this.workflows.filter(wf => wf.bySettingName.toLocaleLowerCase() !== workflowSetting.name.toLocaleLowerCase());
              }
            });
            if (this.workflows.length > 0 && !SingleOrDefault(this.workflows, 'id', this.selectedRequestTypeGuid)) {
              this.changeWorkflowType(this.workflows[0]);
              if (this.workflows.length === 1) {
                this.disableWorkflowDropdown = true;
              }
            }
          }
          resolve();
        },
        error: (err) => reject(err)
      });
    });
  }

  private setupRequestData() {
    if (!this._request) return;

    if (this.workflows.length === 0) this.getWorkflows(this.authenticationService.organisationGuid);
    this.requestName = this._request.requestName;
    this.requestRequestNameControl.setValue(this.requestName);
    if (this._request.workflowId) {
      this.selectedRequestTypeGuid = this._request.workflowId;
      this.selectedRequestTypeName = SingleOrDefault(this.workflows, "id", this._request.workflowId)?.translationKey || "";

      this.requestWorkflowControl.setValue(this._request.workflowId);
    }
    if (this._request.workgroup) {
      let tempName = this.authenticationService.getWorkGroupName(this._request!.workgroup);
      this.selectedWorkgroupName = tempName != "" ? tempName : this.translateService.instant("Request.RequestTitle.Noworkgroup");
      this.selectedWorkgroupGuid = this._request.workgroup;
      this.selectedClearanceLevel = this._request.clearancelevel;

      this.requestWorkgroupControl.setValue(this._request.workgroup);
      this.requestClearanceControl.setValue(this._request.clearancelevel);
    }

    this.disableOrganisationDropdown = true;
    this.disableWorkflowDropdown = true;
  }

}
